const STORAGE_KEY = "analytics_reporting_tz";
const BROWSER_DEFAULT = "__browser__";

export const REPORTING_TIMEZONE_OPTIONS = [
  BROWSER_DEFAULT,
  "UTC",
  "Europe/London",
  "Europe/Dublin",
  "Europe/Paris",
  "America/New_York",
  "America/Los_Angeles",
] as const;

export type ReportingTimezoneOption = (typeof REPORTING_TIMEZONE_OPTIONS)[number];

export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function resolveReportingTimezone(stored: string): string {
  if (!stored || stored === BROWSER_DEFAULT) return getBrowserTimezone();
  return stored;
}

export function loadStoredReportingTimezone(): string {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && REPORTING_TIMEZONE_OPTIONS.includes(v as ReportingTimezoneOption)) {
      return v;
    }
  } catch {
    /* ignore */
  }
  return BROWSER_DEFAULT;
}

export function saveStoredReportingTimezone(option: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, option);
  } catch {
    /* ignore */
  }
}

export function formatTimezoneOffset(iana: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: iana,
      timeZoneName: "shortOffset",
    }).formatToParts(at);
    const raw = parts.find((p) => p.type === "timeZoneName")?.value ?? "UTC";
    return raw.replace(/^GMT/i, "UTC");
  } catch {
    return "UTC";
  }
}

export function formatTimezoneAbbrev(iana: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: iana,
      timeZoneName: "short",
    }).formatToParts(at);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? iana;
  } catch {
    return iana;
  }
}

/** e.g. `Europe/London (BST, UTC+1)` */
export function formatTimezoneLabel(iana: string, at: Date = new Date()): string {
  if (iana === "UTC") return "UTC (UTC+0)";
  const abbrev = formatTimezoneAbbrev(iana, at);
  const offset = formatTimezoneOffset(iana, at);
  return `${iana} (${abbrev}, ${offset})`;
}

export function formatTimezoneOptionLabel(option: ReportingTimezoneOption): string {
  if (option === BROWSER_DEFAULT) {
    const tz = getBrowserTimezone();
    return `Browser — ${formatTimezoneLabel(tz)}`;
  }
  return formatTimezoneLabel(option);
}

/** Minutes to add to UTC for `iana` at `at` (for legacy `tz_offset` API param). */
export function offsetMinutesForTimezone(iana: string, at: Date = new Date()): number {
  if (iana === "UTC") return 0;
  try {
    const asUtc = new Date(at.toLocaleString("en-US", { timeZone: "UTC" }));
    const asTz = new Date(at.toLocaleString("en-US", { timeZone: iana }));
    return Math.round((asTz.getTime() - asUtc.getTime()) / 60_000);
  } catch {
    return 0;
  }
}

export function isBrowserDefaultOption(stored: string): boolean {
  return stored === BROWSER_DEFAULT;
}

export { BROWSER_DEFAULT as REPORTING_TZ_BROWSER_DEFAULT };
